import { AddUserIcon, AttachIcon, CheckboxRoundActiveIcon } from '@synergy/theme-next';
import { BonusSettlementSupplierStatus } from '@synergy/models';
import { CommentNoteIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { FormikProps, useFormikContext } from 'formik';
import { Paper, Stack, useTheme } from '@mui/material';
import { PoolAttachmentsModal, PoolButton as PoolNextButton, PoolTooltip } from '@synergy/components-next';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import match from 'autosuggest-highlight/match';

import { BonusSettlementDetail } from '../models/bonus-settlement-detail';
import { BonusSettlementDetailCommentModal } from './components/bonus-settlement-detail-comment-modal';
import { BonusSettlementDetailFormValues } from '../models/bonus-settlement-detail-form-values';
import { BonusSettlementDetailSubmitModal } from './components/bonus-settlement-detail-submit-modal';
import { BonusSettlementSupplierDetailAddCustomerModal } from './components/bonus-settlement-detail-add-customer-modal';
import {
    bonusSettlementDetailReadOnlySelector,
    bonusSettlementDetailSubmittedSelector,
} from './store/bonus-settlements-detail-selector';
import { updateSupplierBonusSettlementFiles } from './store/bonus-settlements-detail-actions';
import { useAppSelector } from '../../../store/store';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

interface BonusSettlementDetailFooterProps {
    bonusSettlementDetail: BonusSettlementDetail;
}

export const BonusSettlementDetailFooter = ({ bonusSettlementDetail }: BonusSettlementDetailFooterProps) => {
    const theme = useTheme();
    const dispatch = useThunkDispatch();
    const submitted = useAppSelector(bonusSettlementDetailSubmittedSelector);
    const privateFilesystemPath = useAppSelector(state => state.settings.privateFilesystemPath);
    const readOnly = useAppSelector(bonusSettlementDetailReadOnlySelector);
    const formikProps = useFormikContext<BonusSettlementDetailFormValues>();
    const [openCommentModal, setIsCommentModalOpen] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const documents = bonusSettlementDetail.documents;
    const [filesCount, setFilesCount] = useState<number>(0);

    useEffect(() => {
        if (submitted) {
            setShowSubmitModal(false);
        }
    }, [submitted]);

    useEffect(() => {
        setFilesCount(documents.length);
    }, [documents]);

    const handleAllFinishedClick = (formikProps: FormikProps<BonusSettlementDetailFormValues>) => {
        const search = searchParams.get('search');
        const isFinished = searchParams.get('isFinished') === 'true';

        if (isFinished) {
            return;
        }

        formikProps.values.customerBonusSettlements.forEach((settlement, index) => {
            if (settlement && settlement.isFinished) {
                return;
            }

            if (search) {
                const nameMatches = match(settlement.customerName, search, {
                    requireMatchAll: true,
                    insideWords: true,
                });
                const idMatches = match(settlement.customerId.toString(), search);
                if (nameMatches.length === 0 && idMatches.length === 0) {
                    return;
                }
            }

            formikProps.setFieldValue(`customerBonusSettlements.${index}.isFinished`, true);
        });
    };
    const handleSaveFiles = (uploadedFiles: File[], deletedFileIds: number[]): void => {
        setFilesCount(documents.length + (uploadedFiles.length - deletedFileIds.length));

        dispatch(
            updateSupplierBonusSettlementFiles({
                bonusSettlementId: bonusSettlementDetail.id,
                files: uploadedFiles,
                deletedFileIds,
            })
        );
    };

    const editOrAddTranslationId =
        formikProps.values.noteFromSupplier.length > 0
            ? 'app.bonusSettlements.supplierDetail.editComment'
            : 'app.bonusSettlements.supplierDetail.addComment';

    const readOnlyTranslationId = readOnly ? 'app.bonusSettlements.supplierDetail.showComment' : undefined;
    const commentTranslationId = readOnlyTranslationId ?? editOrAddTranslationId;
    const isNotFinished = formikProps.values.customerBonusSettlements.some(settlement => !settlement.isFinished);
    const canAddCustomer = [
        BonusSettlementSupplierStatus.Open,
        BonusSettlementSupplierStatus.Reclaimed,
        BonusSettlementSupplierStatus.PartiallySent,
    ].includes(bonusSettlementDetail.status);

    return (
        <>
            <Paper
                sx={{
                    py: 2,
                    px: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: theme => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack direction="row" gap={2}>
                    {canAddCustomer && (
                        <PoolNextButton
                            startIcon={<AddUserIcon />}
                            variant="text"
                            onClick={() => setIsAddUserModalOpen(true)}
                        >
                            <FormattedMessage id={'app.bonusSettlements.supplierDetail.addCustomer'} />
                        </PoolNextButton>
                    )}
                    <PoolNextButton
                        startIcon={<CommentNoteIcon />}
                        variant="text"
                        onClick={() => setIsCommentModalOpen(true)}
                    >
                        <FormattedMessage id={commentTranslationId} />
                    </PoolNextButton>
                    <PoolNextButton
                        startIcon={<AttachIcon />}
                        disabled={readOnly && filesCount === 0}
                        variant="text"
                        onClick={() => setIsFilesModalOpen(true)}
                    >
                        <FormattedMessage id="app.common.attachedFilesWithCount" values={{ count: filesCount }} />
                    </PoolNextButton>
                </Stack>

                {!readOnly && (
                    <Stack direction="row" gap={2}>
                        <PoolNextButton
                            startIcon={<CheckboxRoundActiveIcon fillColor={theme.palette.success.main} />}
                            variant="text"
                            onClick={() => handleAllFinishedClick(formikProps)}
                        >
                            <FormattedMessage id="app.bonusSettlements.supplierDetail.allFinishedCheck" />
                        </PoolNextButton>
                        <PoolTooltip
                            title={
                                isNotFinished ? (
                                    <FormattedMessage id="app.bonusSettlements.supplierDetail.submit.tooltip" />
                                ) : (
                                    ''
                                )
                            }
                            PopperProps={{
                                sx: { textAlign: 'center' },
                            }}
                        >
                            <PoolNextButton onClick={() => setShowSubmitModal(true)} disabled={isNotFinished}>
                                <FormattedMessage id="app.bonusSettlements.supplierDetail.submit" />
                            </PoolNextButton>
                        </PoolTooltip>
                    </Stack>
                )}
            </Paper>

            <BonusSettlementDetailCommentModal
                open={openCommentModal}
                onClose={() => setIsCommentModalOpen(false)}
                translationId={commentTranslationId}
            />

            <PoolAttachmentsModal
                downloadBaseUrl={privateFilesystemPath}
                open={isFilesModalOpen}
                files={documents}
                readOnly={readOnly}
                hideActions={readOnly}
                onSave={handleSaveFiles}
                onClose={() => setIsFilesModalOpen(false)}
            />

            <BonusSettlementDetailSubmitModal open={showSubmitModal} onClose={() => setShowSubmitModal(false)} />

            <BonusSettlementSupplierDetailAddCustomerModal
                open={isAddUserModalOpen}
                readOnly={readOnly}
                onClose={() => setIsAddUserModalOpen(false)}
            />
        </>
    );
};
